/* eslint-disable react/jsx-key */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { defaultPickerDate, fetchData } from '../../utils';
import { useSelector } from 'react-redux';
import { CARD_INFO_TEMPLATE } from '../../constants';
import Content from '../../components/Developers/Content';
import MetricsGrid from '../../components/Developers/MetricsGrid';
import DashboardWidgetNew from './DashboardWidgetNew';
import DatePicker from '../../components/DatePicker/Picker';
import DisplayModeToggle from '../../components/DisplayModeToggle';
import { getDateFromMagicKeyword, sendRequest } from '../../utils/dateHelpers';
import {
  getAverageDeveloperCostFromSource,
  parseNumericInput,
  shouldFetchAverageDeveloperCostFromEndpoint
} from '../../utils/companyConfigurations';

const DISPLAY_MODES = {
  REAL_EFFORT: 'git_effort',
  INVESTMENT: 'investment'
};

const getAverageDeveloperCost = userInfo => {
  const directCost = getAverageDeveloperCostFromSource(userInfo);

  if (directCost > 0) return directCost;

  return getAverageDeveloperCostFromSource(userInfo?.user);
};

const convertEffortToInvestment = (value, averageCost) => {
  const effortValue = parseNumericInput(value);
  return ((Number.isFinite(effortValue) ? effortValue : 0) / 60) * averageCost;
};

const TimeRangeSelector = ({ selected, onChange, isDarkMode }) => {
  const buttonBaseClass =
    'rounded-md px-3 py-2 text-xs font-bold transition-colors sm:text-sm';

  const getButtonClass = option =>
    selected === option
      ? isDarkMode
        ? 'bg-boxdark text-white shadow-sm'
        : 'bg-white text-blue-500 shadow-sm'
      : isDarkMode
        ? 'text-slate-400 hover:text-white'
        : 'text-gray-500 hover:text-gray-900';

  return (
    <div
      className={`flex w-full flex-wrap items-stretch gap-1 rounded-lg border p-1 lg:w-auto ${
        isDarkMode
          ? 'border-strokedark bg-meta-4'
          : 'border-stroke bg-[#F8F9FA]'
      }`}
    >
      <button
        onClick={() => onChange('this_year_start')}
        className={`min-w-[calc(50%-0.125rem)] flex-1 sm:min-w-fit ${buttonBaseClass} ${getButtonClass('this_year_start')}`}
      >
        This Year
      </button>
      <button
        onClick={() => onChange('last_12_month')}
        className={`min-w-[calc(50%-0.125rem)] flex-1 sm:min-w-fit ${buttonBaseClass} ${getButtonClass('last_12_month')}`}
      >
        Last 12 Months
      </button>
      <button
        onClick={() => onChange('last_one_month')}
        className={`min-w-[calc(50%-0.125rem)] flex-1 sm:min-w-fit ${buttonBaseClass} ${getButtonClass('last_one_month')}`}
      >
        Last 30 days
      </button>

      <button
        onClick={() => onChange('last_one_week')}
        className={`min-w-[calc(50%-0.125rem)] flex-1 sm:min-w-fit ${buttonBaseClass} ${getButtonClass('last_one_week')}`}
      >
        Last 7 days
      </button>

      <button
        onClick={() => onChange('customDate')}
        className={`w-full sm:w-auto sm:min-w-fit ${buttonBaseClass} ${getButtonClass('customDate')}`}
      >
        Custom Date
      </button>
    </div>
  );
};

const DevelopersOverview = () => {
  const { userInfo = {} } = useSelector(state => state);
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );
  const [filters, setFilters] = useState({
    ...defaultPickerDate,
    operation: 'git_effort',
    calculationsMethod: 'ratio'
  });
  const [loading, setLoading] = useState(true);
  const cardInfo = useState(Object.values(CARD_INFO_TEMPLATE));

  const [responseInsightData, setResponseInsightData] = useState();
  const [selectedTimeRange, setSelectedTimeRange] = useState('last_12_month');
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODES.REAL_EFFORT);
  const [fallbackAverageDeveloperCost, setFallbackAverageDeveloperCost] =
    useState(0);
  const roleBasedAverageDeveloperCost = getAverageDeveloperCost(userInfo);
  const averageDeveloperCost =
    roleBasedAverageDeveloperCost > 0
      ? roleBasedAverageDeveloperCost
      : fallbackAverageDeveloperCost;
  const hasClientSideInvestmentRate = averageDeveloperCost > 0;
  const shouldRequestServerCalculatedInvestment =
    displayMode === DISPLAY_MODES.INVESTMENT && !hasClientSideInvestmentRate;

  const {
    dashboardData: { authors }
  } = userInfo;

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (selectedTimeRange === 'customDate') {
      return;
    }

    setFilters(prev => ({
      ...prev,
      dateRange: {
        startDate: getDateFromMagicKeyword(selectedTimeRange),
        endDate: getDateFromMagicKeyword('now')
      }
    }));
  }, [selectedTimeRange]);

  const handleFilters = filtersTemp => {
    setFilters(filtersTemp);
  };

  const getData = async () => {
    try {
      setLoading(true);

      filters.viewType = 'total';
      const responseInsightDataTemp = await fetchData(
        {
          ...filters,
          operation: shouldRequestServerCalculatedInvestment
            ? DISPLAY_MODES.INVESTMENT
            : filters.operation
        },
        null,
        '/overview/insights'
      );

      if (responseInsightDataTemp?.cards?.lowestPerformerAuthor) {
        responseInsightDataTemp.cards.lowestPerformerAuthor.author_name =
          authors?.[
            responseInsightDataTemp.cards.lowestPerformerAuthor.authord_uid
          ]?.label;
      }
      if (responseInsightDataTemp?.cards?.topPerformerAuthor) {
        responseInsightDataTemp.cards.topPerformerAuthor.author_name =
          authors?.[
            responseInsightDataTemp.cards.topPerformerAuthor.authord_uid
          ]?.label;
      }
      responseInsightDataTemp?.cards?.lowestPerformerAuthor?.authord_uid;
      setLoading(false);
      setResponseInsightData(responseInsightDataTemp);
    } catch (error) {
      console.error('Error sending POST request:', error);
    }
  };

  useEffect(() => {
    if (filters.dateRange) {
      getData();
    }
  }, [filters, shouldRequestServerCalculatedInvestment]);

  useEffect(() => {
    const fetchAverageDeveloperCost = async () => {
      try {
        const response = await sendRequest('/company-configurations', 'GET');
        const fallbackCost = getAverageDeveloperCostFromSource(response);

        if (fallbackCost > 0) {
          setFallbackAverageDeveloperCost(fallbackCost);
        }
      } catch (error) {
        console.error('Failed to load fallback average developer cost', error);
      }
    };

    if (
      roleBasedAverageDeveloperCost > 0 ||
      !shouldFetchAverageDeveloperCostFromEndpoint(
        userInfo?.role,
        userInfo?.user?.role
      )
    ) {
      return;
    }

    fetchAverageDeveloperCost();
  }, [roleBasedAverageDeveloperCost, userInfo?.role, userInfo?.user?.role]);

  const insightDataForDisplay =
    displayMode === DISPLAY_MODES.INVESTMENT && hasClientSideInvestmentRate
      ? {
          ...responseInsightData,
          cards: {
            ...responseInsightData?.cards,
            totalEffort: convertEffortToInvestment(
              responseInsightData?.cards?.totalEffort || 0,
              averageDeveloperCost
            ),
            totalEffortAI: convertEffortToInvestment(
              responseInsightData?.cards?.totalEffortAI || 0,
              averageDeveloperCost
            ),
            totalEffortDev: convertEffortToInvestment(
              responseInsightData?.cards?.totalEffortDev || 0,
              averageDeveloperCost
            ),
            topPerformerAuthor: {
              ...responseInsightData?.cards?.topPerformerAuthor,
              effort: convertEffortToInvestment(
                responseInsightData?.cards?.topPerformerAuthor?.effort || 0,
                averageDeveloperCost
              )
            },
            lowestPerformerAuthor: {
              ...responseInsightData?.cards?.lowestPerformerAuthor,
              effort: convertEffortToInvestment(
                responseInsightData?.cards?.lowestPerformerAuthor?.effort || 0,
                averageDeveloperCost
              )
            }
          },
          top10Authors: Object.fromEntries(
            Object.entries(responseInsightData?.top10Authors || {}).map(
              ([key, value]) => [
                key,
                convertEffortToInvestment(value, averageDeveloperCost)
              ]
            )
          ),
          top10Categories: Object.fromEntries(
            Object.entries(responseInsightData?.top10Categories || {}).map(
              ([key, value]) => [
                key,
                convertEffortToInvestment(value, averageDeveloperCost)
              ]
            )
          ),
          top10Projects: Object.fromEntries(
            Object.entries(responseInsightData?.top10Projects || {}).map(
              ([key, value]) => [
                key,
                convertEffortToInvestment(value, averageDeveloperCost)
              ]
            )
          ),
          top10Repos: Object.fromEntries(
            Object.entries(responseInsightData?.top10Repos || {}).map(
              ([key, value]) => [
                key,
                convertEffortToInvestment(value, averageDeveloperCost)
              ]
            )
          )
        }
      : responseInsightData;

  return (
    <div>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start">
          <TimeRangeSelector
            selected={selectedTimeRange}
            onChange={setSelectedTimeRange}
            isDarkMode={isDarkMode}
          />
          {selectedTimeRange === 'customDate' && (
            <div className="w-full xl:w-auto">
              <DatePicker
                dateRange={filters?.dateRange}
                onChange={dateRange =>
                  handleFilters({
                    ...filters,
                    dateRange
                  })
                }
              />
            </div>
          )}
        </div>
        <DisplayModeToggle value={displayMode} onChange={setDisplayMode} />
      </div>
      <div className="grid grid-cols-12 gap-5 ">
        <div className="col-span-12">
          <MetricsGrid
            cardInfo={cardInfo}
            cardData={insightDataForDisplay?.cards}
            loading={loading}
            authors={authors}
            top10Authors={insightDataForDisplay?.top10Authors || []}
            displayMode={displayMode}
            isDarkMode={isDarkMode}
          />
        </div>
        <Content
          contentData={insightDataForDisplay}
          loading={loading}
          displayMode={displayMode}
          isDarkMode={isDarkMode}
        />

        <div className="col-span-12 -mt-5">
          <DashboardWidgetNew
            category="authors"
            filtersShow={false}
            showCheckbox={false}
            //dashboardFilters={dashboardFilters}
            splineChart={false}
            pieChart={false}
            isVisibleLangugageTags={true}
            defaultFilters={{
              ...filters,
              operation: displayMode
            }}
            isCheckContributionHeatmap={true}
            title={{
              pieTitle: 'Developers - Pie',
              tableColumnName: 'Developers'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DevelopersOverview;
